import { sanitizeHtml } from '../utils/sanitizeHtml';
import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Download } from 'lucide-react';
import BBALoader from './BBALoader';

// Storage object names are `${timestamp}_${originalName}` — recover the
// original name from the URL so downloads aren't called "1784840546702_...".
const downloadNameFromUrl = (url, fallback) => {
  try {
    const base = decodeURIComponent(new URL(url).pathname).split('/').pop();
    return base.replace(/^\d{10,}_/, '') || fallback;
  } catch {
    return fallback;
  }
};

/**
 * In-app renderer for xls/xlsx/csv files. Fetches the file and renders each
 * sheet as an HTML table via SheetJS — no external viewer involved. (The
 * Microsoft Office web viewer can't open Firebase Storage URLs: it normalizes
 * the %2F-encoded path separators into real slashes, which Storage rejects.)
 */
const SpreadsheetViewer = ({ url, title, downloadName }) => {
  const [sheets, setSheets] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [fileBlob, setFileBlob] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSheets(null);
    setActiveSheet(0);
    setFileBlob(null);
    setError(false);
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const buffer = await res.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        if (cancelled) return;
        setFileBlob(
          new Blob([buffer], {
            type: res.headers.get('content-type') || 'application/octet-stream',
          })
        );
        setSheets(
          workbook.SheetNames.map((name) => ({
            name,
            html: XLSX.utils.sheet_to_html(workbook.Sheets[name], { header: '', footer: '' }),
          }))
        );
      } catch (err) {
        console.error('Error rendering spreadsheet:', err);
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  // Re-download from the already-fetched bytes so the save keeps the real
  // filename (the download attribute is ignored on cross-origin URLs).
  const handleDownload = () => {
    if (!fileBlob) return;
    const objectUrl = URL.createObjectURL(fileBlob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = downloadName || downloadNameFromUrl(url, title || 'spreadsheet.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  };

  if (error) {
    return (
      <div className="w-full h-full bg-white flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-gray-700 font-medium">This spreadsheet couldn't be previewed.</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          download={title}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          Download instead
        </a>
      </div>
    );
  }

  if (!sheets) {
    return (
      <div className="w-full h-full bg-white flex items-center justify-center">
        <BBALoader size={40} />
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white flex flex-col">
      <style>{`
        .spreadsheet-viewer table { border-collapse: collapse; background: #fff; }
        .spreadsheet-viewer td { border: 1px solid #e5e7eb; padding: 6px 12px; font-size: 13px; color: #1f2937; white-space: nowrap; }
        .spreadsheet-viewer tr:first-child td { background: #f9fafb; font-weight: 600; }
      `}</style>
      <div className="px-14 py-3 border-b border-gray-200 flex items-center gap-4 min-h-[56px]">
        <h2 className="text-sm font-semibold text-gray-900 truncate min-w-0">{title}</h2>
        {sheets.length > 1 && (
          <div className="flex items-center gap-1 overflow-x-auto">
            {sheets.map((sheet, i) => (
              <button
                key={sheet.name}
                type="button"
                onClick={() => setActiveSheet(i)}
                className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  i === activeSheet
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {sheet.name}
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={handleDownload}
          title="Download spreadsheet"
          className="ml-auto flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
        >
          <Download className="w-3.5 h-3.5" />
          Download
        </button>
      </div>
      <div
        className="spreadsheet-viewer flex-1 overflow-auto p-4"
        // SheetJS-generated table markup from the workbook itself
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(sheets[activeSheet]?.html || '') }}
      />
    </div>
  );
};

export default SpreadsheetViewer;
